import { useCallback, useEffect, useRef, useState } from 'react';
import { AdminConversation } from '../../services/admin/FidesChatAdminService';
import { formatConversationPreviewText } from '../../utils/chatParticipantAddedNote';
import {
  BubbleNotification,
  FIDES_CHAT_BUBBLE_DISMISS_MS,
  getDisplayName,
  shouldShowConversationBubble,
} from './fidesChatTrayUtils';

interface UseFidesChatBubbleNotificationsOptions {
  conversations: AdminConversation[];
  loading: boolean;
  open: boolean;
  isPanelOpen: boolean;
  openConversationId?: string | null;
  currentAdminExternalId: string | null;
}

export function useFidesChatBubbleNotifications({
  conversations,
  loading,
  open,
  isPanelOpen,
  openConversationId,
  currentAdminExternalId,
}: UseFidesChatBubbleNotificationsOptions) {
  const [bubble, setBubble] = useState<BubbleNotification | null>(null);
  const prevConversationsRef = useRef<AdminConversation[]>([]);
  const bubbleDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDone = useRef(false);
  const shownBubbles = useRef<Set<string>>(new Set());

  const dismissBubble = useCallback(() => {
    setBubble(null);
    if (bubbleDismissTimer.current) {
      clearTimeout(bubbleDismissTimer.current);
      bubbleDismissTimer.current = null;
    }
  }, []);

  const showBubble = useCallback(
    (notification: BubbleNotification) => {
      if (bubbleDismissTimer.current) {
        clearTimeout(bubbleDismissTimer.current);
      }
      setBubble(notification);
      bubbleDismissTimer.current = setTimeout(dismissBubble, FIDES_CHAT_BUBBLE_DISMISS_MS);
    },
    [dismissBubble],
  );

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      conversations.forEach((conversation) => {
        if (conversation.lastMessageAt) {
          shownBubbles.current.add(`${conversation.conversationId}:${conversation.lastMessageAt}`);
        }
      });
      prevConversationsRef.current = conversations;
      return;
    }

    const prevMap = new Map(prevConversationsRef.current.map((c) => [c.conversationId, c]));
    const now = Date.now();

    for (const conversation of conversations) {
      if (
        !shouldShowConversationBubble(conversation, prevMap.get(conversation.conversationId), {
          open,
          isPanelOpen,
          openConversationId,
          now,
          shownKeys: shownBubbles.current,
        })
      ) {
        continue;
      }

      const key = `${conversation.conversationId}:${conversation.lastMessageAt ?? conversation.lastMessageText}`;
      shownBubbles.current.add(key);

      showBubble({
        conversationId: conversation.conversationId,
        name: getDisplayName(conversation),
        text: formatConversationPreviewText(conversation, currentAdminExternalId) || 'New message',
      });
      break;
    }

    prevConversationsRef.current = conversations;
  }, [
    conversations,
    loading,
    open,
    isPanelOpen,
    openConversationId,
    showBubble,
    currentAdminExternalId,
  ]);

  useEffect(
    () => () => {
      if (bubbleDismissTimer.current) {
        clearTimeout(bubbleDismissTimer.current);
      }
    },
    [],
  );

  return { bubble, dismissBubble };
}
